// React Dependencies
import React, { useState, useEffect } from 'react';

// External Dependencies
import _ from 'lodash';
import PropTypes from 'prop-types';
import { Chip, makeStyles, TextField } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import { toast } from 'react-toastify';
import classNames from 'classnames';

// Internal Dependencies
import { EscapeSingleQuote, StringToLowercase, Trim } from '../../common/util';
import { IsEmpty, IsString } from '../../common/checks';
import Constants from '../../common/constants';
import Colours from '../../styles/colours';
import apiService from '../../services.js/apiService';

const useStyles = makeStyles(() => ({
  root: {
    '& .MuiInputBase-root': {
      alignItems: 'start',
    },
  },
  approvedTag: {
    backgroundColor: '#2ecc71',
    color:           '#FFFFFF',
  },
  rejectedTag: {
    backgroundColor: '#e74c3c',
    color:           '#FFFFFF',
  },
  pendingTag: {
    backgroundColor: '#95a5a6',
    color:           '#FFFFFF',
  },
}));

const MasterNameChips = (props) => {
  const classes = useStyles();
  const [suggestedTags, setSuggestedTags] = useState([]);
  const [addedTags, setAddedTags] = useState([]);

  const findTagsOfDataAndSetAsAddedTags = async (data) => {
    try {
      // format the tag names needed by server
      const tagNamesDelimited = _.join(data, '||');

      const reviewLocationsData = await apiService.getReviewLocationsByNames({ reviewLocationNames: tagNamesDelimited });
      const reviewLocations = _.map(reviewLocationsData, (reviewLocation) => ({ id: reviewLocation.id, name: reviewLocation.name, tags: reviewLocation.tags }));

      const tags = await apiService.getTagsByNames({ tagNames: tagNamesDelimited });
      const displayTags = [];

      for (const d of data) {
        const reviewLocationFound = _.find(reviewLocations, (reviewLocation) => StringToLowercase(reviewLocation.name) === StringToLowercase(d));

        if (!IsEmpty(reviewLocationFound)) {
          const reviewLocation = { ...reviewLocationFound, isApproved: null };
          const tagFound = _.find(tags, (tag) => StringToLowercase(tag.name) === StringToLowercase(d));

          if (!IsEmpty(tagFound)) {
            displayTags.push({ ...reviewLocation, isApproved: tagFound.isApproved });
          } else {
            displayTags.push(reviewLocation);
          }
        } else {
          displayTags.push({ name: d, isApproved: null });
        }
      }

      setAddedTags(displayTags);
    } catch (error) {
      toast.error(error);
    }
  };

  useEffect(() => {
    if (IsEmpty(props.data)) {
      setAddedTags([]);
    } else {
      // Note: We are expecting props.data to be an array of String of the tag names
      // Get the tag status of each item in the data
      findTagsOfDataAndSetAsAddedTags(props.data);
    }
  }, [props.data]);

  const onInputChanged = async (value) => {
    try {
      const nameSearch = EscapeSingleQuote(value);

      if (!IsEmpty(nameSearch)) {
        const reviewLocations = await apiService.getReviewLocationsSuggest(nameSearch);

        const reviewLocationNames = _.map(reviewLocations, (reviewLocation) => reviewLocation.name);
        // format the tag names needed by server
        const tagNamesDelimited = _.join(reviewLocationNames, '||');
        const tags = await apiService.getTagsByNames({ tagNames: tagNamesDelimited });
        const suggestedReviewLocation = [];

        for (const reviewLocation of reviewLocations) {
          const location = { ...reviewLocation, isApproved: null };
          const tagFound = _.find(tags, (tag) => StringToLowercase(tag.name) === StringToLowercase(location.name));

          if (!IsEmpty(tagFound)) {
            suggestedReviewLocation.push({ ...location, isApproved: tagFound.isApproved });
          } else {
            suggestedReviewLocation.push(location);
          }
        }

        const uniqueTags = _.uniqBy(suggestedReviewLocation, 'name');
        setSuggestedTags(uniqueTags || []);
      }
    } catch (error) {
      toast.error(error);
    }
  };

  const findAndRemoveTag = (values, tagName = '') => {
    // user deleted one of the partner. Find out which is deleted
    const existingTags = props.data.slice();

    for (const index in existingTags) {
      if (!IsEmpty(index)) {
        let deleted = true;
        if (tagName) {
          deleted = tagName === existingTags[index];
        } else {
          for (const value of values) {
            if (value.name === existingTags[index]) {
              deleted = false;
              break;
            }
          }
        }
        if (deleted) {
          // found the partner index to delete
          existingTags.splice(index, 1);
          break;
        }
      }
    }
    // update parent com
    props.onChange(existingTags);

    // Code below is to update items displayed in the UI
    const existingAddedTags = addedTags.slice();
    for (const index in existingAddedTags) {
      if (!IsEmpty(index)) {
        let deleted = true;
        if (tagName) {
          deleted = tagName === existingAddedTags[index].name;
        } else {
          for (const value of values) {
            if (value.name === existingAddedTags[index].name) {
              deleted = false;
              break;
            }
          }
        }
        if (deleted) {
          // found the index to delete
          existingAddedTags.splice(index, 1);
          break;
        }
      }
    }
    setAddedTags(existingAddedTags);
  };

  const addValueIfNew = async (value) => {
    try {
      if (IsString(value)) {
        return toast.warn('Please select the value from dropdown list, new custom value cannot be added');
      }

      const existingTags = props.data.slice();
      let found = false;

      // handle case where user hit 'Enter', which causes input 'value' to be a string
      const enteredValue = IsString(value) ? value : value.name;

      if (IsEmpty(Trim(enteredValue, false, true))) {
        // ignore empty string
        return;
      }

      for (const tag of existingTags) {
        if (tag === enteredValue) {
          found = true;
          break;
        }
      }

      if (!found) {
        // add and update parent com
        existingTags.push(enteredValue);
        props.onChange(existingTags);

        setAddedTags([...addedTags.slice(), value]);
      }
    } catch (error) {
      toast.error('Error in addValueIfNew(): ', error);
    }
  };

  const handleChanges = (values) => {
    try {
      // values will contain the distinct and updated array
      // Note that the data is an array of { name, isApproved }
      if (IsEmpty(values)) {
        props.onChange([]);
        setAddedTags([]);
      } else if (values.length > props.data.length) {
        // new value added will always be the last time
        addValueIfNew(values[values.length - 1]);
      } else {
        findAndRemoveTag(values);
      }
    } catch (err) {
      toast.error(Constants.Error.ReportToIT);
      toast.error(err);
    }
  };

  const handleDelete = (value) => {
    try {
      findAndRemoveTag([], value.name);
    } catch (err) {
      toast.error(Constants.Error.ReportToIT);
      toast.error(err);
    }
  };

  const inputDebounced = _.debounce(onInputChanged, 500);

  return (
    <div className="d-flex flex-column">
      <Autocomplete
        className={classes.root}
        multiple
        options={suggestedTags}
        getOptionLabel={(option) => option.name}
        value={addedTags}
        disableCloseOnSelect
        filterSelectedOptions
        freeSolo
        renderOption={(option) => (
          <>
            <span
              classes={{
                root: classNames(classes.tag, {
                  [classes.approvedTag]: option.isApproved === true,
                  [classes.rejectedTag]: option.isApproved === false,
                  [classes.pendingTag]:  option.isApproved === null,
                }),
              }}
              style={{
                background:
                  option.isApproved === true ? Colours.Green
                    : option.isApproved === false ? Colours.Red
                      : Colours.Gray1,
                color:        Colours.White,
                marginRight:  15,
                width:        10,
                height:       10,
                borderRadius: 10,
              }}
            />
            {option.name}
          </>
        )}
        renderTags={(value, getTagProps) => value.map((option, index) => (
          <Chip
            {...getTagProps({ index })}
            variant="outlined"
            label={option.name}
            classes={{
              root: classNames(classes.tag, {
                [classes.approvedTag]: option.isApproved === true,
                [classes.rejectedTag]: option.isApproved === false,
                [classes.pendingTag]:  option.isApproved === null,
              }),
            }}
            style={{ borderColor: Colours.White, fontSize: 14, cursor: 'pointer' }}
            onDelete={() => handleDelete(option)}
          />
        ))}
        onChange={(event, values) => handleChanges(values)}
        renderInput={(params) => (
          <TextField
            {...params}
            variant="outlined"
            onChange={(e) => inputDebounced(e.target.value)}
            placeholder="Type and hit 'Enter' to add"
            multiline
            rows={5}
          />
        )}
      />
    </div>
  );
};

MasterNameChips.propTypes = {
  data:     PropTypes.arrayOf(PropTypes.string),
  onChange: PropTypes.func.isRequired,
};

MasterNameChips.defaultProps = {
  data: [],
};

export default MasterNameChips;
